import loadable, { OptionsWithoutResolver } from '@loadable/component';
import { waitForever } from 'shared/utils/timer';
import { Analytics } from 'modules/common/util/Analytics';

const RETRY_ATTEMPTED_KEY = 'wcp-retry-attempted';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
const loadableWithRetry = (componentImport: Function, options?: OptionsWithoutResolver<any>) =>
  loadable(async () => {
    const pageHasAlreadyBeenForceRefreshed = sessionStorage.getItem(RETRY_ATTEMPTED_KEY) === 'true';

    try {
      const component = await componentImport();

      sessionStorage.removeItem(RETRY_ATTEMPTED_KEY);

      return component;
    } catch (error) {
      const chunkFailed = isFailedChunkLoad(error.message);

      if (chunkFailed) {
        if (!pageHasAlreadyBeenForceRefreshed) {
          // Getting the 'Loading chunk...' error for the first time
          // Reloading the page in case a deploy happened recently that invalidated prior chunks
          sessionStorage.setItem(RETRY_ATTEMPTED_KEY, 'true');
          Analytics.track(Analytics.FunctionalArea.GENERAL, 'Chunk Reloaded');
          window.location.reload();
          // intentionally hanging here while the above reload happens
          // we don't want execution to continue while the page is reloading (which would flash the error page)
          // so we can't actually return something here (wouldn't be a valid chunk) nor can we let execution continue (would throw the error)
          return waitForever();
        }
        // Getting the 'Loading chunk...' error for a second time
        // Log the event and let the error be thrown
        Analytics.track(Analytics.FunctionalArea.GENERAL, 'Chunk Load Failed After Reload');
      }

      // Retry attempt has already been made and an error is still occurring
      // Clean up the retry key (so that the next component load will get a retry)
      // and throw the error and alert the user normally
      sessionStorage.removeItem(RETRY_ATTEMPTED_KEY);
      throw error;
    }
  }, options);

const chunkFailedMessage = /Loading chunk [\d]+ failed/;
export const isFailedChunkLoad = (errorMsg: string) => chunkFailedMessage.test(errorMsg);

export default loadableWithRetry;
